import React, { useState } from 'react';
import { Marker, Popup, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { fetchNearbyPOIs } from '../../services/poiService';
import api from '../../services/api';
import apiRoutes from '../../../../api/apiRoutes';

export const POI_TYPES = 'bus_station,school,hospital,pharmacy,restaurant,supermarket';

export const poiTypeTranslations = {
  bus_station: 'ایستگاه اتوبوس',
  school: 'مدرسه',
  hospital: 'بیمارستان',
  pharmacy: 'داروخانه',
  restaurant: 'رستوران',
  supermarket: 'سوپرمارکت',
};

const poiIcons = {
  bus_station: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
  }),
  school: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
  }),
  hospital: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
  }),
  default: L.icon({
    iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-violet.png',
    iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
  }),
};

// Rendered inside MapContainer to capture clicks
export function POIMapClickHandler({ onMapClick }) {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng);
    },
  });
  return null;
}

// Renders POI markers — must be placed inside a MapContainer
export function POIMarkers({ pois, onVerify, onEdit, onDelete }) {
  return pois.map((poi) => {
    if (!poi?.geometry?.coordinates || !poi?.properties?.osm_id) return null;

    let coords;
    if (poi.geometry.type === 'MultiPoint') {
      coords = poi.geometry.coordinates[0];
    } else if (poi.geometry.type === 'Point') {
      coords = poi.geometry.coordinates;
    } else return null;

    const [lng, lat] = coords;
    if (isNaN(lat) || isNaN(lng)) return null;

    const { osm_id, type, name, is_verified } = poi.properties;

    return (
      <Marker
        key={`poi-${osm_id}`}
        position={[lat, lng]}
        icon={poiIcons[type] || poiIcons.default}
      >
        <Popup>
          <div className="rtl">
            <strong>{name || poiTypeTranslations[type] || type}</strong>
            <br />
            <span>نوع: {poiTypeTranslations[type] || type}</span>
            <br />
            <span style={{ color: is_verified ? '#28a745' : '#dc3545', fontSize: '0.9em' }}>
              {is_verified ? 'تایید شده' : 'تایید نشده'}
            </span>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
              <button onClick={() => onVerify(osm_id)}
                style={{ padding: '4px 8px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                تایید
              </button>
              <button onClick={() => onEdit(poi)}
                style={{ padding: '4px 8px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                ویرایش
              </button>
              <button onClick={() => onDelete(osm_id)}
                style={{ padding: '4px 8px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                حذف
              </button>
            </div>
          </div>
        </Popup>
      </Marker>
    );
  });
}

// Sidebar panel component
function POIPanel({ pois, setPois, selectedLocation, setSelectedLocation, pickingLocation, setPickingLocation }) {
  const [newPOI, setNewPOI] = useState({ name: '', type: 'restaurant' });
  const [selectedPOIForEdit, setSelectedPOIForEdit] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const refreshPOIs = async (lat, lon) => {
    const data = await fetchNearbyPOIs(lat, lon, 1000, POI_TYPES);
    return (data.features || []).map(f => ({
      ...f,
      properties: { ...f.properties, osm_id: f.properties.osm_id ?? f.id },
    }));
  };

  const handleVerify = async (osmId) => {
    try {
      await api.post(`/pois/${osmId}/verify/`, {});
      if (selectedLocation) {
        setPois(await refreshPOIs(selectedLocation[0], selectedLocation[1]));
      }
      setError(null);
    } catch (err) {
      setError(`خطا در تایید امکان: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleDelete = async (osmId) => {
    try {
      await api.delete(apiRoutes.poi.remove(osmId));
      setPois(prev => prev.filter(p => p.properties.osm_id !== osmId));
      setError(null);
    } catch (err) {
      setError(`خطا در حذف امکان: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!selectedLocation) {
      setError('لطفا ابتدا موقعیت را روی نقشه انتخاب کنید');
      return;
    }
    try {
      await api.post(apiRoutes.poi.create, {
        name: newPOI.name,
        type: newPOI.type,
        latitude: selectedLocation[0],
        longitude: selectedLocation[1],
      });
      setNewPOI({ name: '', type: 'restaurant' });
      setPois(await refreshPOIs(selectedLocation[0], selectedLocation[1]));
      setError(null);
    } catch (err) {
      setError(`خطا در ثبت امکان: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const osmId = selectedPOIForEdit?.properties?.osm_id;
    if (!osmId) return;
    try {
      await api.patch(apiRoutes.poi.detail(osmId), {
        name: selectedPOIForEdit.properties.name,
        type: selectedPOIForEdit.properties.type,
      });
      setSelectedPOIForEdit(null);
      if (selectedLocation) {
        setPois(await refreshPOIs(selectedLocation[0], selectedLocation[1]));
      }
      setError(null);
    } catch (err) {
      setError(`خطا در ویرایش امکان: ${err.response?.data?.error || err.message}`);
    }
  };

  // Expose edit handler so POIMarkers popup can trigger it
  const handleEdit = (poi) => setSelectedPOIForEdit(poi);

  return (
    <div>
      <h3 style={{ fontSize: '14px', marginBottom: '15px' }}>امکانات اطراف</h3>

      {error && (
        <div style={{ padding: '8px', marginBottom: '10px', backgroundColor: '#ff5252', color: '#fff', borderRadius: 4, fontSize: '12px' }}>
          {error}
        </div>
      )}

      {/* Pick location */}
      <div style={{ marginBottom: '16px' }}>
        <button
          onClick={() => setPickingLocation(p => !p)}
          style={{
            width: '100%', padding: '8px',
            background: pickingLocation ? '#1976d2' : '#757575',
            color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '13px',
          }}
        >
          {pickingLocation ? 'روی نقشه کلیک کنید...' : 'انتخاب موقعیت روی نقشه'}
        </button>
        {selectedLocation && (
          <p style={{ fontSize: '11px', color: '#555', marginTop: 4 }}>
            موقعیت: {selectedLocation[0].toFixed(5)}, {selectedLocation[1].toFixed(5)}
          </p>
        )}
      </div>

      {loading && <p style={{ fontSize: '12px', color: '#666' }}>در حال بارگذاری...</p>}

      {/* POI list */}
      {pois.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <h4 style={{ fontSize: '13px', marginBottom: '8px' }}>امکانات یافت شده ({pois.length})</h4>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {pois.map(poi => {
              const { osm_id, name, type, is_verified } = poi.properties;
              return (
                <div key={osm_id} style={{ padding: '6px 8px', marginBottom: 6, background: '#f8f9fa', borderRadius: 4, fontSize: '12px' }}>
                  <strong>{name || poiTypeTranslations[type] || type}</strong>
                  <span style={{ marginRight: 6, color: '#666' }}>{poiTypeTranslations[type] || type}</span>
                  <span style={{ color: is_verified ? '#28a745' : '#dc3545', fontSize: '11px', marginRight: 4 }}>
                    {is_verified ? '✓' : '○'}
                  </span>
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    <button onClick={() => handleVerify(osm_id)}
                      style={{ padding: '2px 6px', background: '#28a745', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: '11px' }}>
                      تایید
                    </button>
                    <button onClick={() => handleEdit(poi)}
                      style={{ padding: '2px 6px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: '11px' }}>
                      ویرایش
                    </button>
                    <button onClick={() => handleDelete(osm_id)}
                      style={{ padding: '2px 6px', background: '#dc3545', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer', fontSize: '11px' }}>
                      حذف
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Add new POI */}
      <div style={{ borderTop: '1px solid #eee', paddingTop: '12px' }}>
        <h4 style={{ fontSize: '13px', marginBottom: '8px' }}>افزودن امکان جدید</h4>
        <form onSubmit={handleCreate}>
          <input
            type="text"
            placeholder="نام"
            value={newPOI.name}
            onChange={e => setNewPOI(p => ({ ...p, name: e.target.value }))}
            style={{ width: '100%', padding: '6px', fontSize: '12px', marginBottom: 8 }}
          />
          <select
            value={newPOI.type}
            onChange={e => setNewPOI(p => ({ ...p, type: e.target.value }))}
            style={{ width: '100%', padding: '6px', fontSize: '12px', marginBottom: 8 }}
          >
            {Object.entries(poiTypeTranslations).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <button type="submit"
            style={{ width: '100%', padding: '8px', background: '#4CAF50', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: '12px' }}>
            ثبت امکان جدید
          </button>
        </form>
      </div>

      {/* Edit modal */}
      {selectedPOIForEdit && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
        }}>
          <div style={{ background: '#fff', padding: 20, borderRadius: 8, width: 320, direction: 'rtl' }}>
            <h3 style={{ fontSize: '14px', marginBottom: 12 }}>ویرایش امکان</h3>
            <form onSubmit={handleEditSubmit}>
              <input
                type="text"
                value={selectedPOIForEdit.properties.name || ''}
                onChange={e => setSelectedPOIForEdit(p => ({ ...p, properties: { ...p.properties, name: e.target.value } }))}
                style={{ width: '100%', padding: '6px', marginBottom: 8 }}
                placeholder="نام"
              />
              <select
                value={selectedPOIForEdit.properties.type || ''}
                onChange={e => setSelectedPOIForEdit(p => ({ ...p, properties: { ...p.properties, type: e.target.value } }))}
                style={{ width: '100%', padding: '6px', marginBottom: 12 }}
              >
                {Object.entries(poiTypeTranslations).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit"
                  style={{ flex: 1, padding: '8px', background: '#007bff', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                  ذخیره
                </button>
                <button type="button" onClick={() => setSelectedPOIForEdit(null)}
                  style={{ flex: 1, padding: '8px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}>
                  انصراف
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default POIPanel;
